import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatCurrency } from '@/lib/utils';
import { useInMemoryCostAnalysis } from './hooks/useInMemoryCostAnalysis';
import { useLineItemManualOverride } from '@/hooks/useLineItemManualOverride';
import { useCustomLineItem } from './hooks/useCustomLineItem';
import { getDisplayDescription, hasManualOverrides, getComponentTypeBadge, calculateUnitFinalFromMargin, calculateMarginFromUnitFinal } from '@/utils/lineItemDisplayHelpers';
import { DollarSign, TrendingUp, BarChart3, Wrench, RotateCcw, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import AddCustomItemForm, { CustomItemData } from './components/AddCustomItemForm';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { FilterRule } from '@/types/adjustment-filters';

interface InMemoryCostAnalysisModalProps {
  lineItems: any[] | undefined;
  isOpen: boolean;
  onClose: () => void;
  estimateStatus?: string;
  estimateId?: string;
  discountPercentage?: number;
  adjustmentFilters?: FilterRule[];
}

const InMemoryCostAnalysisModal: React.FC<InMemoryCostAnalysisModalProps> = ({
  lineItems,
  isOpen,
  onClose,
  estimateStatus = 'draft',
  estimateId,
  discountPercentage,
  adjustmentFilters
}) => {
  const costAnalysis = useInMemoryCostAnalysis(lineItems, discountPercentage, adjustmentFilters);
  const { updateManualDescription, updateManualUnitCost, resetPricing } = useLineItemManualOverride();
  const { addCustomLineItem, deleteLineItem } = useCustomLineItem({ estimateId: estimateId || '' });
  const queryClient = useQueryClient();
  
  // Local state for inline editing
  const [editingDescription, setEditingDescription] = useState<Record<string, string>>({});
  const [editingUnitFinal, setEditingUnitFinal] = useState<Record<string, number | undefined>>({});
  const [editingMargin, setEditingMargin] = useState<Record<string, number | undefined>>({});
  const [showResetAllDialog, setShowResetAllDialog] = useState(false);
  const [resetAllConfirmText, setResetAllConfirmText] = useState('');
  const [showAddCustomItemForm, setShowAddCustomItemForm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; description: string } | null>(null);
  
  // Check if estimate is sent/approved (block editing)
  const isReadOnly = estimateStatus !== 'draft';
  
  // Handlers for inline editing
  const handleSaveDescription = async (itemId: string) => {
    const newDescription = editingDescription[itemId];
    if (!newDescription || !estimateId) return;
    
    await updateManualDescription.mutateAsync({
      lineItemId: itemId,
      newDescription,
      estimateId
    });
    
    // Clear editing state
    const { [itemId]: removed, ...rest } = editingDescription;
    setEditingDescription(rest);
  };
  
  const handleSavePrice = async (itemId: string) => {
    if (!estimateId) return;
    
    const item = costAnalysis?.items.find(i => i.id === itemId);
    if (!item) return;

    // Determine final unit cost from whichever field was edited
    let newUnitCost: number;
    
    if (editingUnitFinal[itemId] !== undefined) {
      newUnitCost = Math.max(0, editingUnitFinal[itemId]!);
    } else if (editingMargin[itemId] !== undefined) {
      newUnitCost = calculateUnitFinalFromMargin(item.unitBaseCost, Math.max(0, editingMargin[itemId]!));
    } else {
      return; // Nothing to save
    }

    // Validate
    if (newUnitCost < 0) {
      return;
    }

    // For custom items, update unit_cost_snapshot and total_cost_snapshot directly
    // because manual_unit_cost holds the BASE cost for custom items
    if (item.componentType === 'custom') {
      const newTotalCost = newUnitCost * item.quantity;
      const { error } = await supabase
        .from('estimate_line_items')
        .update({
          unit_cost_snapshot: newUnitCost,
          total_cost_snapshot: newTotalCost
        })
        .eq('id', itemId);

      if (error) {
        toast.error('Failed to update price');
      } else {
        toast.success('Price updated');
        queryClient.invalidateQueries({ queryKey: ['estimateLineItems', estimateId] });
      }
    } else {
      await updateManualUnitCost.mutateAsync({
        lineItemId: itemId,
        newUnitCost,
        quantity: item.quantity,
        estimateId
      });
    }
    
    // Clear editing state
    setEditingUnitFinal({ ...editingUnitFinal, [itemId]: undefined });
    setEditingMargin({ ...editingMargin, [itemId]: undefined });
  };

  const handleUnitFinalChange = (itemId: string, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      const clamped = Math.max(0, numValue);
      setEditingUnitFinal({ ...editingUnitFinal, [itemId]: clamped });
      setEditingMargin({ ...editingMargin, [itemId]: undefined });
    }
  };

  const handleMarginChange = (itemId: string, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      const clamped = Math.max(0, numValue);
      setEditingMargin({ ...editingMargin, [itemId]: clamped });
      setEditingUnitFinal({ ...editingUnitFinal, [itemId]: undefined });
    }
  };

  const getLiveUnitFinal = (itemId: string, item: any): number => {
    if (editingUnitFinal[itemId] !== undefined) return editingUnitFinal[itemId]!;
    if (editingMargin[itemId] !== undefined) {
      return calculateUnitFinalFromMargin(item.unitBaseCost, editingMargin[itemId]!);
    }
    return item.unitFinalCost;
  };

  const getLiveMargin = (itemId: string, item: any): number => {
    if (editingMargin[itemId] !== undefined) return editingMargin[itemId]!;
    if (editingUnitFinal[itemId] !== undefined) {
      return calculateMarginFromUnitFinal(item.unitBaseCost, editingUnitFinal[itemId]!);
    }
    return item.margin;
  };
  
  const handleResetPricing = async (itemId: string) => {
    if (!estimateId) return;
    
    await resetPricing.mutateAsync({
      lineItemId: itemId,
      estimateId
    });
  };
  
  const handleResetAllPricing = async () => {
    if (!estimateId || !lineItems) return;
    
    const overriddenItems = lineItems.filter(item => item.manual_override);
    
    for (const item of overriddenItems) {
      await resetPricing.mutateAsync({
        lineItemId: item.id,
        estimateId
      });
    }
    
    setShowResetAllDialog(false);
    setResetAllConfirmText('');
  };
  
  const getBadgeForType = (componentType: string) => {
    const badge = getComponentTypeBadge(componentType);
    return <Badge variant={badge.variant} className={badge.className}>{badge.label}</Badge>;
  };

  const handleAddCustomItem = async (data: CustomItemData) => {
    await addCustomLineItem.mutateAsync(data);
    setShowAddCustomItemForm(false);
  };

  const handleDeleteLineItem = async () => {
    if (!itemToDelete) return;
    await deleteLineItem.mutateAsync(itemToDelete.id);
    setItemToDelete(null);
  };

  if (!costAnalysis || !lineItems || lineItems.length === 0) {
    return (
      <>
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent className="max-w-6xl h-[90vh] flex flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle>Cost Analysis</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <p className="text-muted-foreground">No line items to analyze</p>
              {!isReadOnly && estimateId && (
                <Button
                  variant="outline"
                  onClick={() => setShowAddCustomItemForm(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Custom Item
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Add Custom Item Form */}
        <AddCustomItemForm
          isOpen={showAddCustomItemForm}
          onClose={() => setShowAddCustomItemForm(false)}
          onSubmit={handleAddCustomItem}
          isSubmitting={addCustomLineItem.isPending}
        />
      </>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-7xl h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Cost Analysis (Current Cart)
            </DialogTitle>
            <DialogDescription>
              View and edit line item descriptions and pricing. Changes trigger versioning.
            </DialogDescription>
          </DialogHeader>
          
          {/* Discount applied banner */}
          {costAnalysis.discountPercentage > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-shrink-0">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-blue-600" />
                <p className="text-sm font-medium text-blue-900">
                  Discount Applied: {costAnalysis.discountPercentage}% (-{formatCurrency(costAnalysis.discountAmount)})
                </p>
              </div>
            </div>
          )}

          {/* Read-only warning banner */}
          {isReadOnly && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex-shrink-0">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-900">This estimate has been sent</p>
                  <p className="text-sm text-amber-700">Create a new version to make changes</p>
                </div>
              </div>
            </div>
          )}

          <Tabs defaultValue="items" className="w-full flex-1 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <TabsList className="grid w-full grid-cols-3 max-w-md">
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="categories">By Category</TabsTrigger>
                <TabsTrigger value="items">All Items</TabsTrigger>
              </TabsList>
              
              {!isReadOnly && lineItems && lineItems.some(item => item.manual_override) && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowResetAllDialog(true)}
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset All Pricing
                </Button>
              )}
            </div>

            <TabsContent value="summary" className="space-y-6 flex-1 overflow-y-auto">
              {/* Overall Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Base Cost</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-orange-600">
                      {formatCurrency(costAnalysis.totalBaseCost)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Your actual costs
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Customer Price</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(costAnalysis.discountPercentage > 0 ? costAnalysis.totalFinalCostAfterDiscount : costAnalysis.totalFinalCost)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {costAnalysis.discountPercentage > 0 
                        ? `After ${costAnalysis.discountPercentage}% discount` 
                        : 'Price to customer'}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Profit Amount</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {formatCurrency(
                        (costAnalysis.discountPercentage > 0 ? costAnalysis.totalFinalCostAfterDiscount : costAnalysis.totalFinalCost) 
                        - costAnalysis.totalBaseCost
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {costAnalysis.discountPercentage > 0 ? 'After discount' : 'Your profit'}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Profit Margin</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-600">
                      {(costAnalysis.discountPercentage > 0 ? costAnalysis.overallMarginAfterDiscount : costAnalysis.overallMargin).toFixed(2)}%
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {(costAnalysis.discountPercentage > 0 ? costAnalysis.overallMarkupAfterDiscount : costAnalysis.overallMarkup).toFixed(2)}% markup
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Category Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Category Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead className="text-right">Base Cost</TableHead>
                        <TableHead className="text-right">Final Cost</TableHead>
                        <TableHead className="text-right">Profit</TableHead>
                        <TableHead className="text-right">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {costAnalysis.categorySummaries.map((category) => (
                        <TableRow key={category.categoryName}>
                          <TableCell className="font-medium">{category.categoryName}</TableCell>
                          <TableCell>{category.itemCount}</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(category.totalBaseCost)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(category.totalFinalCost)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(category.totalFinalCost - category.totalBaseCost)}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={category.averageMargin > 20 ? 'text-green-600' : category.averageMargin > 10 ? 'text-yellow-600' : 'text-red-600'}>
                              {category.averageMargin.toFixed(2)}%
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="categories" className="space-y-6 flex-1 overflow-y-auto">
              {costAnalysis.categorySummaries.map((category) => {
                const categoryItems = costAnalysis.items.filter(item => item.category === category.categoryName);
                
                return (
                  <Card key={category.categoryName}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{category.categoryName}</span>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-muted-foreground">
                            {category.itemCount} items
                          </span>
                          <span className="font-medium">
                            Profit: {formatCurrency(category.totalFinalCost - category.totalBaseCost)} 
                            ({category.averageMargin.toFixed(2)}%)
                          </span>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[300px]">
                        <div className="overflow-x-auto">
                          <Table className="min-w-[800px]">
                            <TableHeader>
                              <TableRow>
                                <TableHead className="min-w-[250px]">Description</TableHead>
                                <TableHead className="min-w-[100px]">Type</TableHead>
                                <TableHead className="text-center min-w-[70px]">Qty</TableHead>
                                <TableHead className="text-right min-w-[100px]">Unit Base</TableHead>
                                <TableHead className="text-right min-w-[120px]">Unit Final</TableHead>
                                <TableHead className="text-right min-w-[110px]">Total Base</TableHead>
                                <TableHead className="text-right min-w-[120px]">Total Final</TableHead>
                                <TableHead className="text-right min-w-[100px]">Margin %</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {categoryItems.map((item) => {
                                const lineItem = lineItems?.find(li => li.id === item.id);
                                
                                return (
                                  <TableRow key={item.id}>
                                    <TableCell>
                                      <TooltipProvider>
                                        <div className="flex items-center gap-2">
                                          {lineItem && hasManualOverrides(lineItem) && (
                                            <Tooltip>
                                              <TooltipTrigger>
                                                <Wrench className="h-4 w-4 text-amber-500 flex-shrink-0" />
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>Custom description or pricing</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          )}
                                          {isReadOnly ? (
                                            <span>{lineItem ? getDisplayDescription(lineItem) : item.description}</span>
                                          ) : (
                                            <Input
                                              value={editingDescription[item.id] ?? (lineItem ? getDisplayDescription(lineItem) : item.description)}
                                              onChange={(e) => setEditingDescription({ ...editingDescription, [item.id]: e.target.value })}
                                              onBlur={() => handleSaveDescription(item.id)}
                                              disabled={updateManualDescription.isPending}
                                              className="max-w-md"
                                            />
                                          )}
                                          {item.componentType === 'assembly' && !isReadOnly && (
                                            <Tooltip>
                                              <TooltipTrigger>
                                                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>This is an assembly. Editing the description won't change the underlying components.</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          )}
                                        </div>
                                      </TooltipProvider>
                                    </TableCell>
                                    <TableCell>{getBadgeForType(item.componentType)}</TableCell>
                                    <TableCell className="text-center">{item.quantity.toFixed(2)}</TableCell>
                                    <TableCell className="text-right text-muted-foreground">{formatCurrency(item.unitBaseCost)}</TableCell>
                                    <TableCell className="text-right">
                                      {isReadOnly ? (
                                        <span>{formatCurrency(item.unitFinalCost)}</span>
                                      ) : (
                                        <div className="flex items-center justify-end gap-1">
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={editingUnitFinal[item.id] ?? item.unitFinalCost.toFixed(2)}
                                            onChange={(e) => handleUnitFinalChange(item.id, e.target.value)}
                                            onBlur={() => handleSavePrice(item.id)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleSavePrice(item.id)}
                                            disabled={updateManualUnitCost.isPending}
                                            className="max-w-[110px] text-right"
                                          />
                                          {lineItem?.manual_override && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleResetPricing(item.id)}
                                              disabled={resetPricing.isPending}
                                            >
                                              <RotateCcw className="h-4 w-4" />
                                            </Button>
                                          )}
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                      {formatCurrency(item.unitBaseCost * item.quantity)}
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                      {formatCurrency(getLiveUnitFinal(item.id, item) * item.quantity)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {isReadOnly ? (
                                        <span className={item.margin > 20 ? 'text-green-600' : item.margin > 10 ? 'text-yellow-600' : 'text-red-600'}>
                                          {item.margin.toFixed(2)}%
                                        </span>
                                      ) : (
                                        <div className="flex items-center justify-end gap-1">
                                          <Input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            max="99"
                                            value={editingMargin[item.id] ?? item.margin.toFixed(2)}
                                            onChange={(e) => handleMarginChange(item.id, e.target.value)}
                                            onBlur={() => handleSavePrice(item.id)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleSavePrice(item.id)}
                                            disabled={updateManualUnitCost.isPending}
                                            className={`max-w-[80px] text-right ${
                                              getLiveMargin(item.id, item) > 20 ? 'text-green-600' : 
                                              getLiveMargin(item.id, item) > 10 ? 'text-yellow-600' : 'text-red-600'
                                            }`}
                                          />
                                        </div>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                );
              })}
            </TabsContent>

            <TabsContent value="items" className="space-y-6 flex-1 overflow-y-auto">
              <Card>
                <CardHeader>
                  <CardTitle>All Line Items</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[500px]">
                    <div className="overflow-x-auto">
                      <Table className="min-w-[1000px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="min-w-[250px]">Description</TableHead>
                            <TableHead className="min-w-[100px]">Category</TableHead>
                            <TableHead className="min-w-[100px]">Type</TableHead>
                            <TableHead className="text-center min-w-[70px]">Qty</TableHead>
                            <TableHead className="text-right min-w-[100px]">Unit Base</TableHead>
                            <TableHead className="text-right min-w-[120px]">Unit Final</TableHead>
                            <TableHead className="text-right min-w-[110px]">Total Base</TableHead>
                            <TableHead className="text-right min-w-[120px]">Total Final</TableHead>
                            <TableHead className="text-right min-w-[100px]">Margin %</TableHead>
                            {!isReadOnly && <TableHead className="w-[60px]"></TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {costAnalysis.items.map((item) => {
                            const lineItem = lineItems?.find(li => li.id === item.id);
                            
                            return (
                              <TableRow key={item.id}>
                                <TableCell>
                                  <TooltipProvider>
                                    <div className="flex items-center gap-2">
                                      {lineItem && hasManualOverrides(lineItem) && (
                                        <Tooltip>
                                          <TooltipTrigger>
                                            <Wrench className="h-4 w-4 text-amber-500 flex-shrink-0" />
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>Custom description or pricing</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                      {isReadOnly ? (
                                        <span>{lineItem ? getDisplayDescription(lineItem) : item.description}</span>
                                      ) : (
                                        <Input
                                          value={editingDescription[item.id] ?? (lineItem ? getDisplayDescription(lineItem) : item.description)}
                                          onChange={(e) => setEditingDescription({ ...editingDescription, [item.id]: e.target.value })}
                                          onBlur={() => handleSaveDescription(item.id)}
                                          disabled={updateManualDescription.isPending}
                                          className="max-w-md"
                                        />
                                      )}
                                      {item.componentType === 'assembly' && !isReadOnly && (
                                        <Tooltip>
                                          <TooltipTrigger>
                                            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>This is an assembly. Editing the description won't change the underlying components.</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </TooltipProvider>
                                </TableCell>
                                <TableCell>{item.category}</TableCell>
                                <TableCell>{getBadgeForType(item.componentType)}</TableCell>
                                <TableCell className="text-center">{item.quantity.toFixed(2)}</TableCell>
                                <TableCell className="text-right text-muted-foreground">{formatCurrency(item.unitBaseCost)}</TableCell>
                                <TableCell className="text-right">
                                  {isReadOnly ? (
                                    <span>{formatCurrency(item.unitFinalCost)}</span>
                                  ) : (
                                    <div className="flex items-center justify-end gap-1">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editingUnitFinal[item.id] ?? item.unitFinalCost.toFixed(2)}
                                        onChange={(e) => handleUnitFinalChange(item.id, e.target.value)}
                                        onBlur={() => handleSavePrice(item.id)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSavePrice(item.id)}
                                        disabled={updateManualUnitCost.isPending}
                                        className="max-w-[110px] text-right"
                                      />
                                      {lineItem?.manual_override && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleResetPricing(item.id)}
                                          disabled={resetPricing.isPending}
                                        >
                                          <RotateCcw className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="text-right text-muted-foreground">
                                  {formatCurrency(item.unitBaseCost * item.quantity)}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {formatCurrency(getLiveUnitFinal(item.id, item) * item.quantity)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {isReadOnly ? (
                                    <span className={item.margin > 20 ? 'text-green-600' : item.margin > 10 ? 'text-yellow-600' : 'text-red-600'}>
                                      {item.margin.toFixed(2)}%
                                    </span>
                                  ) : (
                                    <div className="flex items-center justify-end gap-1">
                                      <Input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="99"
                                        value={editingMargin[item.id] ?? item.margin.toFixed(2)}
                                        onChange={(e) => handleMarginChange(item.id, e.target.value)}
                                        onBlur={() => handleSavePrice(item.id)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSavePrice(item.id)}
                                        disabled={updateManualUnitCost.isPending}
                                        className={`max-w-[80px] text-right ${
                                          getLiveMargin(item.id, item) > 20 ? 'text-green-600' : 
                                          getLiveMargin(item.id, item) > 10 ? 'text-yellow-600' : 'text-red-600'
                                        }`}
                                      />
                                    </div>
                                  )}
                                </TableCell>
                                {!isReadOnly && (
                                  <TableCell>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setItemToDelete({ id: item.id, description: item.description })}
                                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </ScrollArea>
                  
                  {/* Add Custom Item Button */}
                  {!isReadOnly && estimateId && (
                    <div className="mt-4 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => setShowAddCustomItemForm(true)}
                        className="w-full"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Custom Item
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
      
      {/* Reset All Pricing Confirmation Dialog */}
      <AlertDialog open={showResetAllDialog} onOpenChange={setShowResetAllDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset All Pricing?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset all manual pricing overrides to their default values. 
              Descriptions will not be affected. This action cannot be undone.
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Type <span className="font-bold">RESET</span> to confirm:</p>
                <Input
                  value={resetAllConfirmText}
                  onChange={(e) => setResetAllConfirmText(e.target.value.toUpperCase())}
                  placeholder="RESET"
                  className="uppercase"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowResetAllDialog(false);
              setResetAllConfirmText('');
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetAllPricing}
              disabled={resetAllConfirmText !== 'RESET' || resetPricing.isPending}
            >
              {resetPricing.isPending ? 'Resetting...' : 'Reset All Pricing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!itemToDelete} onOpenChange={(open) => !open && setItemToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Line Item?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "<span className="font-medium">{itemToDelete?.description}</span>"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLineItem}
              disabled={deleteLineItem.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLineItem.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Add Custom Item Form */}
      <AddCustomItemForm
        isOpen={showAddCustomItemForm}
        onClose={() => setShowAddCustomItemForm(false)}
        onSubmit={handleAddCustomItem}
        isSubmitting={addCustomLineItem.isPending}
      />
    </>
  );
};

export default InMemoryCostAnalysisModal;
